/**
 * Workspace shell: fixed sidebar + top bar + scrolling content.
 *
 * Shared by the tenant admin and the platform console, which are the two surfaces that
 * are TOOLS rather than pages. Both carry more destinations than a horizontal bar can
 * hold — admin has a dozen across content, people, engagement and settings; the console
 * grows one group per platform concern — and a nav that hides where you are is the thing
 * to avoid in something people work in all day. The learner keeps its own header/footer
 * shell (`Shell`), because three destinations do fit in a bar.
 *
 * It exists as one component rather than two similar ones because the second copy is
 * where they drift: the console's own bar-only chrome had already diverged from admin's
 * on collapse behaviour, active-state colour and scope chip, and neither was wrong —
 * they were just different, in a product where the two are meant to feel like one system.
 *
 * Everything that differs between the two arrives as props:
 *   brand    the mark and the two-line label — the console's is fixed, admin's is the
 *            tenant's, so neither is named in here
 *   groups   the nav, declared by the caller in one place, so adding a surface is one
 *            entry and the nav never advertises a page that does not exist
 *   scope    the permanent "what am I acting on" chip. NOT decoration: an admin tool
 *            that looks identical across academies is how people edit the wrong one,
 *            and the console's version is the only cue that a change lands everywhere
 *   actions  the right-hand end of the top bar (account menu, sign out, exit links)
 *
 * The sidebar's active state is a near-black pill rather than the brand colour, kept
 * from the prototype's `.settings__nav li.is-active`: in a dense tool the brand colour
 * is already working in status chips and primary buttons, and "where am I" should not
 * have to compete with it. That also makes the same component read correctly under both
 * palettes, since near-black is not a branded value.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Icon } from '../ui';
import './workspace.css';

export interface WorkspaceNavItem {
  to: string;
  label: string;
  /** Material Symbols name. The sidebar is icon-only when collapsed, so this is required. */
  icon: string;
}

export interface WorkspaceNavGroup {
  heading: string;
  items: WorkspaceNavItem[];
}

export interface WorkspaceBrand {
  /** Usually a single letter or an icon. Kept as a node so neither caller has to fake one. */
  mark: ReactNode;
  title: string;
  subtitle: string;
  /** Where the mark links to — each surface's own index. */
  home: string;
}

export function WorkspaceShell({
  brand,
  groups,
  scope,
  actions,
  storageKey,
}: {
  brand: WorkspaceBrand;
  groups: WorkspaceNavGroup[];
  scope: { icon: string; label: string };
  actions?: ReactNode;
  /**
   * localStorage key for the collapse state.
   *
   * Per surface, not shared: the console and the admin are different windows of work,
   * and collapsing one is not a statement about the other. Persisted at all because
   * someone working in a narrow window should not have to re-collapse on every
   * navigation.
   */
  storageKey: string;
}) {
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(storageKey) === 'collapsed',
  );
  useEffect(() => {
    localStorage.setItem(storageKey, collapsed ? 'collapsed' : 'expanded');
  }, [collapsed, storageKey]);

  // Which items need react-router's `end`, derived rather than declared.
  //
  // Only an item that is a PREFIX of another item does: without `end`, admin's `/admin`
  // stays highlighted on every child page, and with it, the console's `/tenants` stops
  // being highlighted on `/tenants/new` — where the operator plainly is still in Tenants.
  // Asking each caller to get that right per item is asking for one of the two bugs.
  const paths = groups.flatMap((group) => group.items.map((item) => item.to));
  const isPrefix = (to: string) => paths.some((other) => other.startsWith(`${to}/`));

  return (
    <div className="workspace" data-collapsed={collapsed || undefined}>
      <aside className="workspace__side" aria-label="Sections">
        <div className="workspace__brand">
          <NavLink to={brand.home} className="workspace__brand-mark">
            {brand.mark}
          </NavLink>
          <span className="workspace__brand-text">
            {brand.title}
            <small>{brand.subtitle}</small>
          </span>
        </div>

        <nav className="workspace__nav">
          {groups.map((group) => (
            <div className="workspace__group" key={group.heading}>
              <span className="workspace__group-heading">{group.heading}</span>
              <ul>
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={isPrefix(item.to)}
                      className={({ isActive }) => (isActive ? 'is-active' : '')}
                      // Survives collapse — the label is hidden, not removed, so this
                      // is the tooltip for an icon-only rail.
                      title={item.label}
                    >
                      <Icon name={item.icon} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <button
          type="button"
          className="workspace__collapse"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={collapsed ? 'chevron_right' : 'chevron_left'} />
          <span>Collapse</span>
        </button>
      </aside>

      <div className="workspace__main">
        <header className="workspace__top">
          <span className="workspace__scope">
            <Icon name={scope.icon} />
            {scope.label}
          </span>

          <div className="workspace__top-spacer" />

          {actions}
        </header>

        {/* Keyed on the path so a page's own state does not survive a navigation —
            without it, a filter typed on one listing reappears on the next. */}
        <main className="workspace__content" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Standard page header for every workspace surface.
 *
 * Exported here so each page states its own title, description and actions in the same
 * shape — the alternative is two dozen pages that each invent their own heading spacing.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="workspace__page-head">
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="workspace__page-actions">{actions}</div>}
    </header>
  );
}
